import { useEffect, useState } from 'react'
import { triageApi } from '../api'
import { useI18n } from '../i18n'
import type { DashboardStats, TriageSummary } from '../types'

/**
 * Deux niveaux de lecture, et non quatre tuiles équivalentes.
 *
 * Un responsable conformité doit repérer en un coup d'œil ce qui exige une
 * action. La bande du haut n'agrège que cela ; les indicateurs de santé, qui
 * n'appellent aucune décision, passent en dessous et en plus discret.
 *
 * Le principe qui gouverne la mise en page : le plus gros chiffre de l'écran
 * doit être celui qui demande une décision.
 */

interface Props {
  stats: DashboardStats | null
  loading: boolean
  /** Ouvre l'onglet correspondant quand on clique sur un chiffre. */
  onNavigate?: (target: 'alerts' | 'suspended' | 'webscan' | 'discovery') => void
}

/** Seuil de retard, aligne sur `overdueAfterDays` du vocabulaire de triage. */
const OVERDUE_DAYS = 15

export function StatsBar({ stats, loading, onNavigate }: Props) {
  const { t, fmt } = useI18n()
  const [webscan, setWebscan] = useState<TriageSummary | null>(null)
  const [discovery, setDiscovery] = useState<TriageSummary | null>(null)

  useEffect(() => {
    triageApi.getSummary('webscan').then(setWebscan).catch(() => setWebscan(null))
    triageApi.getSummary('discovery').then(setDiscovery).catch(() => setDiscovery(null))
  }, [])

  if (loading || !stats) {
    return (
      <>
        <div className="triage-strip triage-strip-skeleton" />
        <div className="stats-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="stat-card stat-skeleton" />
          ))}
        </div>
      </>
    )
  }

  /** Une détection « ouverte » est encore NEW ou TO_REVIEW : personne ne l'a traitée. */
  const untreated = (s: TriageSummary | null) =>
    s ? (s.byStatus.NEW ?? 0) + (s.byStatus.TO_REVIEW ?? 0) : 0

  const actionable = [
    {
      key: 'alerts' as const,
      value: stats.openAlerts,
      label: t('stats.openAlerts'),
      tone: 'crit' as const,
    },
    {
      key: 'suspended' as const,
      value: stats.suspendedMerchants,
      label: t('stats.suspendedMerchants'),
      tone: 'warn' as const,
    },
    {
      key: 'discovery' as const,
      value: untreated(discovery),
      label: t('stats.discoveryToQualify'),
      tone: 'warn' as const,
    },
    {
      key: 'webscan' as const,
      value: untreated(webscan),
      label: t('stats.webscanToQualify'),
      tone: 'warn' as const,
    },
  ]

  const totalToDo = actionable.reduce((n, a) => n + a.value, 0)
  const overdue = (webscan?.overdue ?? 0) + (discovery?.overdue ?? 0)

  const detectionsTotal = (webscan?.total ?? 0) + (discovery?.total ?? 0)
  const health = [
    {
      label: t('stats.monitoredMerchants'),
      value: fmt.number(stats.activeMerchants),
      sub: t('stats.newThisMonth', { n: stats.newMerchantsThisMonth }),
    },
    {
      label: t('stats.crawls24h'),
      value: fmt.number(stats.crawlsLast24h),
      sub: t('stats.successRate', { rate: stats.crawlSuccessRate }),
    },
    {
      label: t('stats.handledDetections'),
      value: fmt.number(detectionsTotal - totalToDo),
      sub: t('stats.outOfTotal', { n: fmt.number(detectionsTotal) }),
    },
  ]

  return (
    <>
      {/* Niveau 1 — ce qui exige une action */}
      <div className={`triage-strip${totalToDo === 0 ? ' triage-strip-clear' : ''}`}>
        {totalToDo === 0 ? (
          <p className="triage-strip-clear-msg">{t('stats.clear')}</p>
        ) : (
          <>
            <div className="triage-strip-items">
              {actionable
                .filter((a) => a.value > 0)
                .map((a) => (
                  <button
                    key={a.key}
                    type="button"
                    className={`triage-strip-item triage-strip-${a.tone}`}
                    onClick={() => onNavigate?.(a.key)}
                    disabled={!onNavigate}
                  >
                    <span className="triage-strip-value">{fmt.number(a.value)}</span>
                    <span className="triage-strip-label">{a.label}</span>
                  </button>
                ))}
            </div>
            {overdue > 0 && (
              <p className="triage-strip-overdue">
                {t('stats.overduePrefix')} <strong>{fmt.number(overdue)}</strong>{' '}
                {t('stats.overdueSuffix', { days: OVERDUE_DAYS })}
              </p>
            )}
          </>
        )}
      </div>

      {/* Niveau 2 — état de santé, aucune décision attendue */}
      <div className="stats-grid stats-grid-health">
        {health.map((c) => (
          <div key={c.label} className="stat-card stat-card-health">
            <div className="stat-label">{c.label}</div>
            <div className="stat-value">{c.value}</div>
            <div className="stat-sub stat-sub-neutral">{c.sub}</div>
          </div>
        ))}
      </div>
    </>
  )
}
